import type { ActivityOverviewDomain } from '@sparkapp/shared-types';
import { cn } from '@/lib/utils';

interface DomainTilesProps {
  domains: ActivityOverviewDomain[];
  totalActivities: number;
  selectedKey?: string | null;
  onSelect?: (key: string | null) => void;
}

/**
 * One tile per domain in its brand colours: activity count, share of the
 * catalogue and skill coverage (active skills used as a primary-domain skill).
 * Clicking a tile selects that domain; clicking it again clears the selection.
 */
export function DomainTiles({ domains, totalActivities, selectedKey = null, onSelect }: DomainTilesProps) {
  return (
    <div className="space-y-3">
      {onSelect && (
        <button
          type="button"
          aria-pressed={selectedKey === null}
          onClick={() => onSelect(null)}
          className={cn(
            'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
            selectedKey === null
              ? 'border-pine-pine bg-pine-pine text-white'
              : 'border-pine-line bg-white text-pine-ink hover:bg-pine-hover-button'
          )}
        >
          All domains
        </button>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {domains.map((domain) => {
          const covered = domain.skills.filter((s) => s.primaryCount > 0).length;
          const hasGap = covered < domain.skills.length || domain.activityCount === 0;
          const share = totalActivities > 0 ? (domain.activityCount / totalActivities) * 100 : null;
          const stroke = domain.style?.strokeColor ?? 'var(--pine-pine)';
          const isSelected = domain.key === selectedKey;

          return (
            <button
              type="button"
              key={domain.id}
              aria-pressed={isSelected}
              disabled={!onSelect}
              onClick={() => onSelect?.(isSelected ? null : domain.key)}
              className={cn(
                'rounded-pine-card border border-pine-line p-4 shadow-pine-card bg-pine-card text-left transition-shadow',
                'enabled:hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-pine',
                isSelected && 'ring-2 ring-offset-1'
              )}
              style={{
                borderLeftWidth: 4,
                borderLeftColor: stroke,
                backgroundColor: domain.style?.fillColor,
                ...(isSelected ? { ['--tw-ring-color' as string]: stroke } : {}),
              }}
            >
              <p className="text-sm font-medium text-pine-ink truncate" title={domain.title}>
                {domain.title}
              </p>
              <p className="mt-1 text-heading-3 font-semibold tabular-nums text-pine-ink">
                {domain.activityCount}
              </p>
              <div className="mt-2 h-1.5 rounded-full bg-white/70">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${share ?? 0}%`, backgroundColor: stroke }}
                />
              </div>
              <p className="mt-1 text-caption text-pine-ink-soft tabular-nums">
                {share === null ? '—' : `${Math.round(share)}%`} of activities
              </p>
              <p
                className={
                  hasGap
                    ? 'mt-1 text-caption font-medium text-amber-800'
                    : 'mt-1 text-caption text-pine-ink-soft'
                }
              >
                {hasGap && '⚠ '}
                {covered}/{domain.skills.length} skills covered
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
